import ReactCalendar from 'react-calendar';

import { endOfWeek, startOfWeek } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { formatDate } from 'lib/helpers/dateUtils';
import { cn, ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';

import Icon from 'components/shared/Icon/Icon';

import { formatShortWeekday } from './lib/formatShortWeekday';

import './lib/Calendar.scss';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const isDateOnlyString = (v) => typeof v === 'string' && DATE_ONLY_RE.test(v);

// UTC ISO -> Date whose LOCAL wall-clock matches tz
const utcToWallClockDate = (utcISO, tz) => {
  if (!utcISO) return null;
  return toZonedTime(new Date(utcISO), tz);
};

// YYYY-MM-DD -> Date (no timezone shift)
const dateOnlyToDate = (dateStr) => {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(y, mo - 1, d);
};

/**
 * @param {import('./lib/propTypes').CalendarProps} props
 */

const Calendar = (props) => {
  const {
    value,
    onChange,
    isRange = false,
    isCompact = false,
    isBorder = false,
    className,
    showWeekNumbers = false,
    selectWeekOnClick = false,
    firstDayOfWeek = 0,
    timezone: timezoneProp // optional override
  } = props;

  const practiceTimezone = useTimezone({ enabled: !timezoneProp });
  const tz = timezoneProp || practiceTimezone;

  const isRanged = isRange || selectWeekOnClick;

  if (isRange && selectWeekOnClick)
    throw new Error('isRange and selectWeekOnClick should not be true at the same time.');

  // Mirror input type so date-only values don't become timestamps (and shift days)
  const returnDateOnly = (() => {
    if (ia(value)) return value.some(isDateOnlyString);
    return isDateOnlyString(value);
  })();

  const toCalendarValue = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (isDateOnlyString(v)) return dateOnlyToDate(v);
    return utcToWallClockDate(v, tz); // assume UTC ISO
  };

  const calendarValue = ia(value)
    ? isRanged
      ? [toCalendarValue(value?.[0]), toCalendarValue(value?.[1])]
      : toCalendarValue(value?.[0])
    : toCalendarValue(value);

  const calendarDateToCanonical = (pickerDate) => {
    if (!pickerDate) return null;

    const y = pickerDate.getFullYear();
    const mo = pickerDate.getMonth();
    const d = pickerDate.getDate();

    if (returnDateOnly) {
      return formatDate(new Date(y, mo, d), null, 'yyyy-MM-dd');
    }

    // calendar is date-only; represent as midnight in practice tz -> UTC ISO
    return fromZonedTime(new Date(y, mo, d, 0, 0, 0, 0), tz).toISOString();
  };

  const emitChange = (next) => {
    if (!onChange) return;

    if (ia(next)) {
      const [start, end] = next;
      onChange([calendarDateToCanonical(start), calendarDateToCanonical(end)]);
      return;
    }

    onChange(calendarDateToCanonical(next));
  };

  const handleWeekClick = (date) => {
    if (!selectWeekOnClick || !onChange) return;

    // interpret clicked DAY as a day in practice tz (don't instant-convert)
    const baseDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const weekOptions = { weekStartsOn: firstDayOfWeek };

    const weekStartDate = startOfWeek(baseDay, weekOptions);
    const weekEndDate = endOfWeek(baseDay, weekOptions);

    if (returnDateOnly) {
      onChange([
        formatDate(weekStartDate, null, 'yyyy-MM-dd'),
        formatDate(weekEndDate, null, 'yyyy-MM-dd')
      ]);
    } else {
      onChange([
        fromZonedTime(weekStartDate, tz).toISOString(),
        fromZonedTime(weekEndDate, tz).toISOString()
      ]);
    }
  };

  return (
    <ReactCalendar
      selectRange={isRange}
      value={calendarValue}
      className={cn(
        className,
        isCompact && 'is-compact',
        isBorder && 'is-border',
        selectWeekOnClick && 'is-week'
      )}
      onChange={!selectWeekOnClick ? emitChange : undefined}
      formatShortWeekday={formatShortWeekday}
      onClickDay={(v) => handleWeekClick(v)}
      showWeekNumbers={showWeekNumbers}
      onClickWeekNumber={(weekNumber, date) => handleWeekClick(date)}
      calendarType={firstDayOfWeek === 1 ? 'iso8601' : 'gregory'}
      prevLabel={<Icon icon="chevron-left-linear" size={16} />}
      nextLabel={<Icon icon="chevron-right-linear" size={16} />}
      prev2Label={null}
      next2Label={null}
    />
  );
};

export default Calendar;
